import {useEffect} from 'react';
import {isEqual} from 'lodash';
import {useShallow} from 'zustand/react/shallow';
import {useDocumentContext} from '@/components/document/context';
import {useDocumentStore} from '@/components/document/store';
import {DocumentInputField, DocumentValue, Option, ValueResolver, VisibilitySet} from '@/engine';

const shouldSetValue = (
  field: DocumentInputField,
  storedValue: DocumentValue | DocumentValue[],
  newValue: DocumentValue,
) => {
  return field.readonly && field.value != undefined && !isEqual(newValue, storedValue);
};

const getCurrentValue = (
  field: DocumentInputField,
  valueResolver: ValueResolver,
  visibilitySet: VisibilitySet,
): DocumentValue => {
  if (!field.value) return '';

  if (['checkbox', 'radio', 'combobox', 'select'].includes(field.type)) {
    return field.value as Option['value'];
  }

  return valueResolver(field.value, {keepDomainReference: true, visibilitySet: visibilitySet});
};

export const useFieldValue = (field: DocumentInputField) => {
  const {ready, valueResolver, visibilitySet} = useDocumentContext();
  const [storedValue, updateField] = useDocumentStore(
    useShallow(({data, updateField}) => [data[field.id], updateField]),
  );
  const currentValue = getCurrentValue(field, valueResolver, visibilitySet);
  const valueToSet = shouldSetValue(field, storedValue, currentValue) && currentValue;

  useEffect(() => {
    // Wait for the store subscription so dependants of this field get recomputed
    if (!ready || valueToSet === false) return;

    updateField(field.id, valueToSet ?? '');
  }, [ready, valueToSet, updateField, field.id]);
};
